<script lang="ts">
    import type Vehicles from "@/schemas/public/Vehicles";
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type {Document, Field, SectionLinkType, AuditLog} from "@/types/types";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import Debug from "@/components/Debug.svelte";
    import {useForm} from '@inertiajs/svelte';
    import InlineEditable from "@/components/Inputs/InlineEditable.svelte";

    import FormBlock from "@/components/Form/FormBlock.svelte";
    import Popover from "@/components/Popover.svelte";
    import AppLayout from "@/layouts/AppLayout.svelte";
    import { Pencil, Plus, Trash, Car, FileText, MessageSquare, Pickaxe, History } from 'lucide-svelte';
    import AuditLogComponent from '@/components/AuditLogComponent.svelte';
    import EditVehicleModal from "@/components/vehicles/EditVehicleModal.svelte";
    import CreateVehicleModal from "@/components/vehicles/CreateVehicleModal.svelte";
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
    import CreateDocumentModal from "@/components/documents/CreateDocumentModal.svelte";
    import RelatedDocuments from "@/components/documents/RelatedDocuments.svelte";
    import RelatedVehicleAssignments from "@/components/vehicles/RelatedVehicleAssignments.svelte";
    import Comments from '@/components/Comments.svelte';
    import { page } from '@inertiajs/svelte';
    import type { Comment } from "@/types/types";

    type Props = {
        vehicle: Vehicles;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        relatedDocuments?: Document[];
        comments: Comment[];
        entityFieldColumn?: string;
        auditLogs?: AuditLog[];
        jobTypeOptions?: { value: string; label: string }[];
    };

    let {vehicle, blocks, documentBlocks, relatedDocuments, comments, entityFieldColumn, auditLogs = [], jobTypeOptions = []}: Props = $props();

    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (vehicle as Record<string, any>)[entityFieldColumn]
        ? (vehicle as Record<string, any>)[entityFieldColumn]
        : 'Vehicle Details';

    let breadcrumbs = $state([
        {title: 'Vehicles', href: route('vehicles.index')},
        {title: entityDisplayName, href: route('vehicles.show', {vehicle: vehicle.id})},
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        {
            title: 'Edit Vehicle',
            href: undefined,
            onClick: openEditVehicleModal,
            icon: Pencil,
        },
        {
            title: 'Add New Vehicle',
            href: undefined,
            icon: Plus,
            onClick: openCreateVehicleModal,
        },
        {
            title: 'Delete Vehicle',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('vehicles.destroy', {vehicle: vehicle.id}));
            },
            icon: Trash,
        }
    ]);

    let editVehicleModal = $state(false);

    function openEditVehicleModal() {
        editVehicleModal = true;
    }


    let showCreateVehicleModal = $state(false);

    function openCreateVehicleModal() {
        showCreateVehicleModal = true;
    }

    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }



    let inlineFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }
    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);

    // Section links for quick navigation
    const sectionLinks: SectionLinkType[] = [
        { id: 'section-assignments', label: 'Job Assignments', icon: Pickaxe },
        { id: 'section-documents', label: 'Documents', icon: FileText },
        { id: 'section-comments', label: 'Comments', icon: MessageSquare },
        { id: 'section-audit-log', label: 'History', icon: History },
    ];
</script>
<!--<Debug {formInner} {form} {$form}/>-->

<svelte:head>
    <title>Vehicle - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons} {sectionLinks}>
    <FormContainer>
        {#each blocks as block}
            <FormBlock label={block.label}>
                {#each block.fields as field}
                    <InlineEditable
                        field={field}
                        bind:editingFieldId={editingFieldId}
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                                $inlineForm[field.column_name] = newValue;
                                $inlineForm.put(route("vehicles.update", { vehicle }), {
                                    onSuccess: () => {
                                        editingFieldId = undefined;
                                    },
                                    onError: () => {

                                    }
                                });
                                // editingFieldId = undefined;
                            }
                        }
                        record={vehicle}
                    />
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>

    <RelatedVehicleAssignments id="section-assignments" column="vehicle_id" recordId={vehicle.id} {jobTypeOptions} />

    <RelatedDocuments id="section-documents" {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Vehicle"
            relatedRecordId={vehicle.id}
            relatedRecordLabel={vehicle.registration}
        />
    </RelatedDocuments>

    <Comments
        id="section-comments"
        commentableId={vehicle.id}
        commentableType="App\Models\Vehicle"
        {comments}
        currentUser={$page.props.auth.user}
    />

    <AuditLogComponent id="section-audit-log" {auditLogs} />

    <CreateVehicleModal
        blocks={blocks}
        bind:show={showCreateVehicleModal}
    />
    <EditVehicleModal
        {blocks}
        bind:show={editVehicleModal}
        {vehicle}
    />

</AppLayout>
